import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  LightBulbIcon,
  TrophyIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import Button from '../components/common/Button';
import toast from 'react-hot-toast';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface PerformanceData {
  weekly_data: Array<{
    week: string;
    start_date: string;
    end_date: string;
    collaborations: number;
    messages: number;
    followers: number;
    profile_views: number;
    opportunity_views: number;
  }>;
  monthly_data: Array<{
    month: string;
    collaborations: number;
    messages: number;
    followers: number;
    profile_views: number;
  }>;
  insights: {
    trending_up: string[];
    trending_down: string[];
    suggestions: string[];
    achievements: string[];
  };
  user_type: string;
}

const DashboardPerformancePage: React.FC = () => {
  const navigate = useNavigate();
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'weekly' | 'monthly'>('weekly');

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async () => {
    try {
      const response = await dashboardAPI.getDetailedPerformance();
      setPerformanceData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !performanceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // console.log("performanceData", performanceData)
  // Prepare chart data
  const weeklyChartData = {
    labels: performanceData.weekly_data.map(d => d.week),
    datasets: [
      {
        label: 'Collaborations',
        data: performanceData.weekly_data.map(d => d.collaborations),
        borderColor: 'rgb(20, 184, 166)',
        backgroundColor: 'rgba(20, 184, 166, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Profile Views',
        data: performanceData.weekly_data.map(d => d.profile_views),
        borderColor: 'rgb(251, 146, 60)',
        backgroundColor: 'rgba(251, 146, 60, 0.1)',
        tension: 0.1,
      },
      {
        label: 'New Followers',
        data: performanceData.weekly_data.map(d => d.followers),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const monthlyChartData = {
    labels: performanceData.monthly_data.map(d => d.month),
    datasets: [
      {
        label: 'Collaborations',
        data: performanceData.monthly_data.map(d => d.collaborations),
        borderColor: 'rgb(20, 184, 166)',
        backgroundColor: 'rgba(20, 184, 166, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Profile Views',
        data: performanceData.monthly_data.map(d => d.profile_views),
        borderColor: 'rgb(251, 146, 60)',
        backgroundColor: 'rgba(251, 146, 60, 0.1)',
        tension: 0.1,
      },
      {
        label: 'New Followers',
        data: performanceData.monthly_data.map(d => d.followers),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  return (
    <div className="bg-gray-50 h-full overflow-y-auto px-6">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Performance Analytics</h1>
          <p className="text-gray-600">Detailed insights into your ClickCollab activity and growth</p>
        </div>

        {/* Insights Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-5 gap-6 mb-8">
          {/* Trending Up */}
          {performanceData.insights.trending_up.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-green-100 rounded-full">
                  <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-medium text-gray-900">Trending Up</h3>
              </div>
              <div className="space-y-2">
                {performanceData.insights.trending_up.map((insight, index) => (
                  <p key={index} className="text-sm text-gray-600">{insight}</p>
                ))}
              </div>
            </motion.div>
          )}

          {/* Achievements */}
          {performanceData.insights.achievements.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-yellow-100 rounded-full">
                  <TrophyIcon className="h-5 w-5 text-yellow-600" />
                </div>
                <h3 className="font-medium text-gray-900">Achievements</h3>
              </div>
              <div className="space-y-2">
                {performanceData.insights.achievements.map((achievement, index) => (
                  <p key={index} className="text-sm text-gray-600">{achievement}</p>
                ))}
              </div>
            </motion.div>
          )}

          {/* Suggestions */}
          {performanceData.insights.suggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <LightBulbIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-900">Suggestions</h3>
              </div>
              <div className="space-y-2">
                {performanceData.insights.suggestions.map((suggestion, index) => (
                  <p key={index} className="text-sm text-gray-600">{suggestion}</p>
                ))}
              </div>
            </motion.div>
          )}

          {/* Areas to Improve */}
          {performanceData.insights.trending_down.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <ArrowTrendingDownIcon className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="font-medium text-gray-900">Areas to Improve</h3>
              </div>
              <div className="space-y-2">
                {performanceData.insights.trending_down.map((trend, index) => (
                  <p key={index} className="text-sm text-gray-600">{trend}</p>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Performance Trends</h2>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('weekly')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'weekly'
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => setActiveTab('monthly')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'monthly'
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="h-80">
            <Line data={activeTab == 'weekly' ? weeklyChartData : monthlyChartData} options={chartOptions} />
          </div>
        </motion.div>

        {/* Detailed Metrics Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Detailed Metrics</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Collaborations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profile Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    New Followers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Messages
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {performanceData.weekly_data.slice().reverse().map((week, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {week.week}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {week.collaborations}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {week.profile_views}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {week.followers}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {week.messages}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPerformancePage;
